import { defineComponent as _defineComponent } from 'vue'
import { store } from '@/store';
import { delay } from 'lodash';
import DomService from '@/core/dom.service';
import Logo from '../shared/Logo.vue';
import MainNavigationButtons from './MainNavigationButtons.vue';
import MainMenuItem from './menu/MainMenuItem.vue';
import MainNavigationService, { NavigationButtonState } from './mainnavigation.service';
import MiniBasketB2B from '@/ui/shared/MiniBasketB2B.vue';
import MiniQuoteBasketB2B from '@/ui/shared/MiniQuoteBasketB2B.vue';
import MiniBasketB2C from '@/ui/shared/MiniBasketB2C.vue';
import TrackingService from '@/core/tracking.service';
import PortalTopBar from '@/ui/chrome/PortalTopBar.vue';
import PointOfSaleTopBar from '@/ui/chrome/PointOfSaleTopBar.vue';
import viewportEventsService from '@/core/responsive/viewport/viewport-events.service';
import NavigationSearch from '@/ui/shared/NavigationSearch.vue';
import PrivateOrProfessionalSelector from './PrivateOrProfessionalSelector.vue';
import UspItem from './UspItem.vue';
import resolveConfig from 'tailwindcss/resolveConfig';
import tailwindConfig from '../../../tailwind.config.js';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router/composables';
import Http from '@/core/http/http.service';


export default /*#__PURE__*/_defineComponent({
  __name: 'MainNavigation',
  setup(__props) {

const isAuthenticated = computed<boolean>(() => store.getters['app/isAuthenticated']);
const isB2BAuthenticated = computed<boolean>(() => store.getters['app/isB2BAuthenticated']);
const isPointOfSaleMode = computed<boolean>(() => store.getters['app/isPointOfSaleMode']);
const showAuthTopBar = computed<boolean>(() => store.getters['app/showAuthTopBar']);
const showEmptyTopBar = computed<boolean>(() => store.getters['app/showEmptyTopBar']);

const isInEditMode = computed<boolean>(() => store.getters['app/isInEditMode']);
const isCheckoutView = computed<boolean>(() => store.getters['app/isCheckoutView']);
const isBasketVisible = computed<boolean>(() => store.getters['app/isBasketVisible']);
const isQuoteBasketVisible = computed<boolean>(() => store.getters['app/isQuoteBasketVisible']);
const hideSiteModeProf = computed<boolean>(() => store.getters['app/hideSiteModeProf']);
const showBodyPadding = computed<boolean>(() => store.getters['app/showBodyPadding']);
const menu = computed<INavigationViewObject[]>(() => store.getters['app/menu']);
const isOnSearchPage = computed<StringFunction>(() => store.getters['app/isOnSearchPage']);
const isCheckoutViewWithTopBar = computed<boolean>(() => store.getters['app/isCheckoutViewWithTopBar']);
const menuTheme = computed<MenuThemeSettings>(() => store.getters['app/menuTheme']);

const lineItemsCount = computed<number>(() => store.getters['basket/lineItemsCount']);
const quoteLineItemsCount = computed<number>(() => store.getters['quotebasket/quoteLineItemsCount']);

const isSearchOpen = computed<boolean>(() => store.getters['navigation/isSearchOpen']);
const isMiniBasketOpen = computed<boolean>(() => store.getters['navigation/isMiniBasketOpen']);
const isMiniQuoteBasketOpen = computed<boolean>(() => store.getters['navigation/isMiniQuoteBasketOpen']);

function toggleSearch(canOpenSearch?: boolean) : void {
    store.dispatch('navigation/toggleSearch', canOpenSearch);
}
function toggleBasket() : void {
    store.dispatch('navigation/toggleBasket');
}
function toggleQuoteBasket() : void {
    store.dispatch('navigation/toggleQuoteBasket');
}
function closeNavigationOverlays() : void {
    store.dispatch('navigation/closeNavigationOverlays');
}
function openMegaMenu() : void {
    store.dispatch('navigation/openMegaMenu');
}

const mainNavigation = ref(null);
const mainNavigationContent = ref(null);
const backgroundContainer = ref(null);
const navigationSpacer = ref(null);
const mainNavigationWrapper = ref(null);

const runAnimations = ref<boolean>(false);
const addToBasketSuccess = ref<boolean>(false);
const addToQuoteBasketSuccess = ref<boolean>(false);
const basketSuccessTimer = ref<any>();
const quoteBasketSuccessTimer = ref<any>();

const popoutOpen = ref<boolean>(false);
const lastScrollTop = ref<number>(0);
const lastAnchor = ref<number>(0);
const scrollCallbackIndex = ref<number | undefined>();
const basketIconClicked = ref<boolean>(false);
const initAnimation = 400;

const megamenuMeasurements = ref<MegamenuMeasurements>({
    topMenuCssHeight: '',
    minHeight: '',
    contentHeight: 0,
    searchPopoutMinHeight: ''
});

const backgroundContainerHeight = ref<number>(0);
const backgroundContainerStyle = ref<any>({});

const route = useRoute();
const canOpenSearch = computed<boolean>(() => !isOnSearchPage.value(route.path));

const showNavigation = computed<boolean>(() => !isCheckoutView.value || isCheckoutViewWithTopBar.value);

watch(lineItemsCount, showAddToBasketSuccessAnimation);
watch(quoteLineItemsCount, showAddToQuoteBasketSuccessAnimation);
watch(isSearchOpen, onIsSearchOpenChange);

function onIsSearchOpenChange() {
    if (isSearchOpen.value) {
        setTimeout(() => {
            if (!popoutOpen.value) {
                backgroundContainer.value.style.transitionDuration = '200ms';
                setTimeout(() => backgroundContainer.value.style.transitionDuration = '', 202);
            }
        }, 10);
    } else {
        if (!popoutOpen.value) {
            backgroundContainer.value.style.transitionDuration = '200ms';
            backgroundContainer.value.style.transitionDelay = '100ms';
            setTimeout(() => {
                backgroundContainer.value.style.transitionDuration = '';
                backgroundContainer.value.style.transitionDelay = '';
            }, 502);
        }
    }
}

watch(() => route.fullPath, onUrlChange);

function onUrlChange(): void {
    closeNavigationOverlays();
    calculateBackgroundContainerHeight();
}

function onScroll() {
    if (!showNavigation.value) {
        return;
    }
    // Scroll logic may only be triggered if no modals are open
    if (DomService.hasElementClass('html', 'modal-open')) {
        return;
    }
    const currentScrollTop = viewportEventsService.viewport.scrollY;
    let topPosition = 0;
    if (currentScrollTop > 0 && lastScrollTop.value < currentScrollTop) {
        // Scrolling down
        const mainNavigationHeight = mainNavigation.value.getBoundingClientRect().height;
        const navigationSpacerHeight = navigationSpacer.value ? navigationSpacer.value.getBoundingClientRect().height : 0;
        const height = menuIsWhite.value && !showAuthTopBar.value
            ? backgroundContainer.value.getBoundingClientRect().height
            : mainNavigationHeight + navigationSpacerHeight;
        topPosition = Math.min(height, currentScrollTop - lastAnchor.value);

        mainNavigationWrapper.value.style.transform = 'translateY(' + (-topPosition) + 'px)';
        mainNavigationWrapper.value.style.transition = '';
        toggleMegaMenuPopout(false);
    } else if (lastScrollTop.value > currentScrollTop) {
        lastAnchor.value = currentScrollTop;
        resetWrapperTransform();
    }
    lastScrollTop.value = currentScrollTop;
    recalculateMenuHeight();
}

onMounted(() => {
    const fullConfig = resolveConfig(tailwindConfig);
    megamenuMeasurements.value.minHeight = fullConfig.theme.minHeight.megaMenu;
    const topBarHeight = showAuthTopBar.value
        ? fullConfig.theme.height.topBar.lg.authenticated
        : showEmptyTopBar.value
            ? fullConfig.theme.padding['empty-top-bar']
            : fullConfig.theme.height.topBar.lg.normal;
    megamenuMeasurements.value.topMenuCssHeight = `calc(${topBarHeight} + ${fullConfig.theme.height.topMenu.lg})`;
    megamenuMeasurements.value.searchPopoutMinHeight = fullConfig.theme.minHeight.searchResultPopoutContainer;

    togglePopout(false);
    initMenuAnimations();
    scrollCallbackIndex.value = viewportEventsService.setCallback(onScroll);

    setTimeout(() => {
        document.querySelectorAll('.c-main-navigation__popout-content')
            .forEach(x => { megamenuMeasurements.value.contentHeight = Math.max(megamenuMeasurements.value.contentHeight, x.clientHeight); });
    }, initAnimation);
});

onBeforeUnmount(() => {
    viewportEventsService.removeCallback(scrollCallbackIndex.value);
    scrollCallbackIndex.value = undefined;
});

const navigationButtonState = computed<NavigationButtonState>(() => {
    const loggedIn = isAuthenticated.value;
    const inCheckout = isCheckoutView.value;
    const hideSiteModeProfValue = hideSiteModeProf.value;

    return MainNavigationService.getNavigationButtonState({ loggedIn, inCheckout, hideSiteModeProf: hideSiteModeProfValue });
});

function toggleShoppingCart(): void {
    basketIconClicked.value = true;
    toggleBasket();
    if (isMiniBasketOpen.value) {
        TrackingService.event('Mini page', 'Mini basket');
    }
}

function toggleQuoteCart(): void {
    basketIconClicked.value = true;
    toggleQuoteBasket();
    if (isMiniBasketOpen.value) { TrackingService.event('Mini page', 'Mini Quote basket'); };
}

async function searchIconClicked() {
    const currentScrollTop = viewportEventsService.viewport.scrollY;
    lastScrollTop.value = currentScrollTop;
    lastAnchor.value = currentScrollTop;
    resetWrapperTransform();
    toggleSearch(canOpenSearch.value);
    await calculateBackgroundContainerHeight();
}

function resetWrapperTransform() {
    mainNavigationWrapper.value.style.transform = '';
    mainNavigationWrapper.value.style.transition = 'transform 800ms cubic-bezier(0.73, 0, 0.42, 1)';
}

function initMenuAnimations(): void {
    delay(() => (runAnimations.value = true), initAnimation);
}

function tryClearBasketSuccessTimer(): void {
    if (basketSuccessTimer.value) {
        clearTimeout(basketSuccessTimer.value);
    }
    if (quoteBasketSuccessTimer.value) {
        clearTimeout(quoteBasketSuccessTimer.value);
    }
}

function showAddToBasketSuccessAnimation(): void {
    addToBasketSuccess.value = true;
    tryClearBasketSuccessTimer();
    basketSuccessTimer.value = delay(() => (addToBasketSuccess.value = false), 650);
}

function showAddToQuoteBasketSuccessAnimation(): void {
    addToQuoteBasketSuccess.value = true;
    tryClearBasketSuccessTimer();
    quoteBasketSuccessTimer.value = delay(() => (addToQuoteBasketSuccess.value = false), 650);
}

const whiteText = computed<boolean>(() => {
    return menuTheme.value.whiteMenuText && !menuIsWhite.value;
});

const menuIsWhite = computed<boolean>(() => {
    return popoutOpen.value || isSearchOpen.value || lastAnchor.value > 0;
});

const splashColors = computed<any>(() => {
    return [
        whiteText.value ? 'u-bg-white u-text-black' : 'u-bg-black u-text-white'
    ];
});

function onClickOutsideMenu(): void {
    if (isSearchOpen.value) {
        closeNavigationOverlays();
    }
}

async function toggleMegaMenuPopout(value: boolean) {
    popoutOpen.value = value;
    value ? openMegaMenu() : closeNavigationOverlays();
    await calculateBackgroundContainerHeight();
}

async function togglePopout(value: boolean) {
    popoutOpen.value = value;
    await calculateBackgroundContainerHeight();
}

async function recalculateMenuHeight() {
    if (!popoutOpen.value) {
        await calculateBackgroundContainerHeight();
    }
}

async function calculateBackgroundContainerHeight(): Promise<void> {
    return new Promise(resolve => {
        setTimeout(() => {
            if (scrollCallbackIndex.value && mainNavigationContent.value) {
                setBackgroundContainerHeight();
            }
        }, 10);
        setTimeout(() => {
            if (scrollCallbackIndex.value && mainNavigation.value && navigationSpacer.value) {
                const mainNavigationHeight = mainNavigation.value.getBoundingClientRect().height;
                const visibleMainNavigationHeight = menuIsWhite.value ? mainNavigationHeight : -10;
                navigationSpacer.value.style.transform = 'translateY(' + visibleMainNavigationHeight + 'px)';
                if (!popoutOpen.value) {
                    // Hide navigation spacer when popout is closing
                    navigationSpacer.value.style.opacity = 0;
                    navigationSpacer.value.style.transition = 'opacity 400ms cubic-bezier(.73, 0, .42, 1)';
                }
            }
        }, popoutOpen.value ? 10 : 600); // Align with c-main-navigation__popout-content opacity transition duration
        // Reset navigation spacer transition
        setTimeout(() => {
            if (scrollCallbackIndex.value && mainNavigation.value && navigationSpacer.value) {
                navigationSpacer.value.style.opacity = '';
                navigationSpacer.value.style.transition = '';
            }
        }, 610);
        return resolve();
    });
}

function setBackgroundContainerHeight(): void {
    const minHeight = viewportToPixels(megamenuMeasurements.value.minHeight);
    const oldHeight = backgroundContainerHeight.value;
    const newHeight = getBackgroundContainerHeight(minHeight);
    backgroundContainerHeight.value = newHeight;

    const minTransitionDurationMs = 200;
    const extraTransitionDurationMs = 200;
    const transitionDuration = minTransitionDurationMs + (Math.min(minHeight, Math.abs(newHeight - oldHeight)) / minHeight) * extraTransitionDurationMs;
    const transition = `height ${transitionDuration}ms cubic-bezier(0.73, 0, 0.42, 1)`;
    const transitionDelay = popoutOpen.value ? '' : '200ms';
    backgroundContainerStyle.value = { transition: `${transition} ${transitionDelay}`, height: newHeight + 'px' };

    setTimeout(() => {
        backgroundContainerStyle.value.transition = transition;
    }, 50);
}

function getBackgroundContainerHeight(minHeight: number): number {
    const mainNavigationContentHeight = mainNavigationContent.value.getBoundingClientRect().height;
    const visibleMainNavigationContentHeight = menuIsWhite.value ? mainNavigationContentHeight : 0;
    if (!menuIsWhite.value) {
        return 0;
    }
    if (!popoutOpen.value) {
        return visibleMainNavigationContentHeight;
    }
    if (!isSearchOpen.value) {
        return megamenuMeasurements.value.contentHeight > 0
            ? Math.max(minHeight, visibleMainNavigationContentHeight + megamenuMeasurements.value.contentHeight)
            : minHeight;
    }
    return Math.max(minHeight, visibleMainNavigationContentHeight + Number(megamenuMeasurements.value.searchPopoutMinHeight.replace('px', '')));
}

function viewportToPixels(value) {
    const parts = value.match(/([0-9\.]+)(vh)/);
    const q = Number(parts[1]);
    return viewportEventsService.viewport.height * (q / 100);
}

return { __sfc: true,isAuthenticated, isB2BAuthenticated, isPointOfSaleMode, showAuthTopBar, showEmptyTopBar, isInEditMode, isCheckoutView, isBasketVisible, isQuoteBasketVisible, hideSiteModeProf, showBodyPadding, menu, isOnSearchPage, isCheckoutViewWithTopBar, menuTheme, lineItemsCount, quoteLineItemsCount, isSearchOpen, isMiniBasketOpen, isMiniQuoteBasketOpen, toggleSearch, toggleBasket, toggleQuoteBasket, closeNavigationOverlays, openMegaMenu, mainNavigation, mainNavigationContent, backgroundContainer, navigationSpacer, mainNavigationWrapper, runAnimations, addToBasketSuccess, addToQuoteBasketSuccess, basketSuccessTimer, quoteBasketSuccessTimer, popoutOpen, lastScrollTop, lastAnchor, scrollCallbackIndex, basketIconClicked, initAnimation, megamenuMeasurements, backgroundContainerHeight, backgroundContainerStyle, route, canOpenSearch, showNavigation, onIsSearchOpenChange, onUrlChange, onScroll, navigationButtonState, toggleShoppingCart, toggleQuoteCart, searchIconClicked, resetWrapperTransform, initMenuAnimations, tryClearBasketSuccessTimer, showAddToBasketSuccessAnimation, showAddToQuoteBasketSuccessAnimation, whiteText, menuIsWhite, splashColors, onClickOutsideMenu, toggleMegaMenuPopout, togglePopout, recalculateMenuHeight, calculateBackgroundContainerHeight, setBackgroundContainerHeight, getBackgroundContainerHeight, viewportToPixels, Logo, MainNavigationButtons, MainMenuItem, NavigationButtonState, MiniBasketB2B, MiniQuoteBasketB2B, MiniBasketB2C, PortalTopBar, PointOfSaleTopBar, NavigationSearch, PrivateOrProfessionalSelector, UspItem }
}

})